import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { Container } from 'react-bootstrap-5';
import { isEmpty, omit } from 'lodash';

import Filters from './components/filters';
import FirstSearchOffcanvas from './components/first_search_offcanvas';
import MapScriptLoader from '../../helpers/map_script_loader';
import MobileViewSwitcher from './components/mobile_view_switcher';
import ResultAndMapViewer from './components/result_and_map_viewer';
import SaveSearchModal from './components/filters/save_search_modal';
import OffMarketPropertyOffcanvas from './components/off_market_property_offcanvas';

import configureStore from 'bundles/connect/components/store/configure';

import {
  loadCriteria,
  loadOffMarketSearchFlag,
  loadOffMarketTableViewFlag
} from 'bundles/connect/helpers/local_storage';
import { getUrlParam, getUrlParams } from './helpers/url_helper';
import EmbeddedMode from '../embedded_mode';
import {
  EXCLUSIVE_LISTING_URL_PARAM,
  OFF_MARKET_URL_PARAM
} from './components/property_exclusive_listing_offcanvas_container';
import { DEFAULT_UI } from '../store/reducers/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons';
import type { Criteria, Product, Profile, Region, SavedSearch } from 'bundles/connect/types';

type SearchProfile = Profile & {
  defaultMapType?: string;
  hasUnlimitedUnlocks: boolean;
  id?: number;
  location: {
    boundingBox: number[][] | null;
    coordinates: number[];
  } | null;
  regions: Region[];
  residential: boolean;
}

type SearchProps = {
  firstSearch: boolean;
  embeddedMode: boolean;
  nominatimUrl: string;
  offMarketSearch?: boolean;
  products: Product;
  profile: SearchProfile;
  profileSavedSearch: boolean;
  savedSearch?: SavedSearch;
  searchCriteria?: Criteria;
  searchTableColumnKeys?: string[];
}

export default function Search({
  firstSearch,
  embeddedMode,
  nominatimUrl,
  offMarketSearch,
  products,
  profile,
  profileSavedSearch,
  savedSearch,
  searchCriteria,
  searchTableColumnKeys = []
}: SearchProps) {
  const bc = new BroadcastChannel('connect_search');
  bc.onmessage = e => (e.data == 'RELOAD') && window.location.reload();

  const listingId = getUrlParam('exclusive_listing_id');
  const placeId = getUrlParam('place_id');
  const associatedSearch = getUrlParam('associated_search');
  const query = omit(
    getUrlParams(),
    [
      'place-id',
      EXCLUSIVE_LISTING_URL_PARAM,
      OFF_MARKET_URL_PARAM,
      'search_criteria',
      'associated_search',
      'first_search'
    ]
  );

  let searchCriteriaToLoad = searchCriteria || loadCriteria()?.criteria || {};
  searchCriteriaToLoad = isEmpty(query) ? searchCriteriaToLoad : query;

  if (associatedSearch) {
    searchCriteriaToLoad.assetClassIds = [1, 2, 3, 5, 6, 7, 8, 10];
  }

  if (isEmpty(searchCriteriaToLoad)) {
    searchCriteriaToLoad = {};
    if (!isEmpty(profile.location)) {
      searchCriteriaToLoad.mapBounds = profile.location.boundingBox;
    }

    if (profile.residential) {
      searchCriteriaToLoad.assetClassIds = [10];
    }
  }

  const criteria = placeId ? {} : searchCriteriaToLoad;
  if (savedSearch && savedSearch.insightsPropertyIds) {
    criteria.sortBy = 'lastUpdated:desc';
  }

  offMarketSearch = true;
  if (profile.userType !== 'broker') {
    // Brokers only have access to offMarketSearch, so ignore any stored flags
    offMarketSearch = offMarketSearch !== null ? offMarketSearch : loadOffMarketSearchFlag();
  }

  const store = useMemo(() => configureStore({
    criteria,
    savedSearch: savedSearch || {},
    ui: {
      ...DEFAULT_UI,
      products: products,
      profileSavedSearch: profileSavedSearch,
      searchTableColumnKeys: searchTableColumnKeys,
      showMap: false,
      showTableView: profile.id ? loadOffMarketTableViewFlag() : false,
      offMarketSearch,
      listingId,
      profile: profile,
      embeddedMode: embeddedMode
    }
  }), []);

  return (
    <MapScriptLoader>
      <Provider store={store}>
        <div className="position-absolute w-100 h-100 d-flex flex-column bg-white">
          <EmbeddedMode>
            <div className="d-none d-md-block">
              <Container className="p-0" fluid>
                <h3 className="p-6 mb-0">
                  <FontAwesomeIcon className="me-3" icon={faMapLocationDot} size="xs" />
                  Property Search
                </h3>
              </Container>
            </div>
          </EmbeddedMode>

          <Container className="d-flex flex-column flex-grow-1 overflow-hidden" fluid>
            <Filters />
            <ResultAndMapViewer nominatimUrl={nominatimUrl} />
            <MobileViewSwitcher />
            <SaveSearchModal />
          </Container>
        </div>
        <FirstSearchOffcanvas
          nominatimUrl={nominatimUrl}
          regions={profile.regions}
          show={firstSearch}
        />
        <OffMarketPropertyOffcanvas fromConsole={false} />
      </Provider>
    </MapScriptLoader>
  );
}
